import { useEffect, useState, useCallback } from "react";
import { useAuth, useEtiquetaCritico, useNivel4Overline } from "@/context/AuthContext";
import api, { formatError } from "@/lib/api";
import { PageHeader, ScopeNotice, useScopeGuard, useCan } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ScanSearch } from "lucide-react";
import { IAExplain } from "@/components/IAExplain";
import { toast } from "sonner";

const tipoColor = { KO: "bg-[#FF5625] hover:bg-[#FF5625]", mayor: "bg-[#F59E0B] hover:bg-[#F59E0B]", menor: "bg-slate-400 hover:bg-slate-400" };
const ESTADOS = ["abierto", "en_progreso", "cerrado"];

export default function Gaps() {
  const { user, scopeTenant } = useAuth();
  const krit = useEtiquetaCritico();
  const overlineN4 = useNivel4Overline();
  const canEdit = useCan(user);
  const needsScope = useScopeGuard(user, scopeTenant);
  const [gaps, setGaps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ tipo: "mayor", requisito: "", descripcion: "" });

  const load = useCallback(async () => {
    if (needsScope) return;
    const { data } = await api.get("/gaps");
    setGaps(data);
  }, [needsScope]);
  useEffect(() => { load(); }, [load]);

  const crear = async () => {
    if (!form.descripcion) return toast.error("La descripción es obligatoria");
    try {
      await api.post("/gaps", form);
      toast.success("Gap creado");
      setOpen(false);
      setForm({ tipo: "mayor", requisito: "", descripcion: "" });
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const detectar = async () => {
    try {
      const { data } = await api.post("/gaps/detectar");
      toast.success(`${data.creados} gaps detectados automáticamente`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const cambiarEstado = async (g, estado) => {
    try {
      await api.patch(`/gaps/${g.id}`, { estado });
      toast.success(`Estado: ${estado}`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  if (needsScope) return <ScopeNotice />;

  return (
    <div data-testid="gaps-page">
      <PageHeader overline={overlineN4} title="Análisis de Gaps" help="gaps"
        action={canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={detectar} data-testid="detectar-gaps-btn" className="border-[#0E2A47] text-[#0E2A47]">
              <ScanSearch size={16} className="mr-1" /> Detectar automáticamente
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="nuevo-gap-btn" className="bg-[#FF5625] hover:bg-[#E0451A]"><Plus size={16} className="mr-1" /> Nuevo gap</Button>
              </DialogTrigger>
              <DialogContent className="bg-white" data-testid="gap-dialog">
                <DialogHeader><DialogTitle className="text-[#0E2A47]">Registrar gap</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Tipo</Label>
                      <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                        <SelectTrigger data-testid="gap-tipo" className="mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="KO">{krit}</SelectItem>
                          <SelectItem value="mayor">Mayor</SelectItem>
                          <SelectItem value="menor">Menor</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Requisito</Label>
                      <Input value={form.requisito} onChange={(e) => setForm({ ...form, requisito: e.target.value })}
                        data-testid="gap-requisito" className="mt-1.5" placeholder="4.9.1" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Descripción</Label>
                    <Textarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                      data-testid="gap-descripcion" className="mt-1.5" />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={crear} data-testid="gap-submit" className="bg-[#0E2A47] hover:bg-[#163C65]">Crear</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
      />

      <Card className="border-[#E2E8F0] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F7F8FA]">
              <TableHead>Tipo</TableHead><TableHead>Requisito</TableHead><TableHead>Descripción</TableHead>
              <TableHead>Origen</TableHead><TableHead>Estado</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {gaps.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-[#0E2A47]/50 py-8">Sin gaps registrados.</TableCell></TableRow>}
            {gaps.map((g) => (
              <TableRow key={g.id} data-testid={`gap-row-${g.id}`} className="hover:bg-[#F7F8FA]">
                <TableCell><Badge className={tipoColor[g.tipo]}>{g.tipo === "KO" ? krit : g.tipo}</Badge></TableCell>
                <TableCell className="font-mono text-xs text-[#FF5625]">
                  {g.requisito ? <div className="flex flex-col gap-1"><span>{g.requisito}</span>
                    {g.requisito_retirado && <span className="text-[10px] text-[#EF4444] font-sans">requisito retirado del catálogo</span>}
                    <IAExplain numero={g.requisito} /></div> : "—"}
                </TableCell>
                <TableCell className="text-[#0E2A47]/80 max-w-md">{g.descripcion}</TableCell>
                <TableCell className="text-xs uppercase tracking-wider text-[#0E2A47]/50">{g.origen}</TableCell>
                <TableCell>
                  {canEdit ? (
                    <Select value={g.estado} onValueChange={(v) => cambiarEstado(g, v)}>
                      <SelectTrigger data-testid={`gap-estado-${g.id}`} className="h-8 w-36"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {ESTADOS.map((e) => <SelectItem key={e} value={e}>{e.replace("_", " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : <span className="capitalize">{g.estado.replace("_", " ")}</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
